import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { managerDashboardAPI, leaveAPI, scheduleAdjustmentAPI, timeAdjustmentAPI, shiftChangeAPI, otRequestAPI } from '../../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Users, Clock, Calendar, AlertCircle, CheckCircle, XCircle, UserCheck, TrendingUp, Coffee, ClipboardList, Timer } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const MyTeamPage = () => {
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [approvals, setApprovals] = useState([]);
  
  // Rejection dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // OT Approval dialog (for partial approval)
  const [otApprovalDialogOpen, setOtApprovalDialogOpen] = useState(false);
  const [otApprovalHours, setOtApprovalHours] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, approvalsRes] = await Promise.all([
        managerDashboardAPI.getTeamDashboard(),
        managerDashboardAPI.getApprovals()
      ]);
      setDashboardData(dashRes.data);
      setApprovals(approvalsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    // For OT requests, open dialog for optional partial approval
    if (request.request_type === 'ot_request') {
      setSelectedRequest(request);
      setOtApprovalHours(request.details?.requested_hours?.toString() || '');
      setOtApprovalDialogOpen(true);
      return;
    }
    
    try {
      switch (request.request_type) {
        case 'leave':
          await leaveAPI.approve(request.id);
          break;
        case 'shift_change':
          await shiftChangeAPI.approve(request.id);
          break;
        case 'schedule_adjustment':
          if (request.status === 'manager_approved' && user?.role === 'admin') {
            await scheduleAdjustmentAPI.hrApprove(request.id);
          } else {
            await scheduleAdjustmentAPI.managerApprove(request.id);
          }
          break;
        case 'time_adjustment':
          await timeAdjustmentAPI.approve(request.id);
          break;
        default:
          throw new Error('Unknown request type');
      }
      toast.success('Request approved');
      loadData();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleOtApprove = async () => {
    if (!selectedRequest) return;
    
    try {
      const approvedHours = otApprovalHours ? parseFloat(otApprovalHours) : null;
      await otRequestAPI.approve(selectedRequest.id, approvedHours);
      toast.success(`OT approved: ${approvedHours || selectedRequest.details?.requested_hours}h`);
      setOtApprovalDialogOpen(false);
      setSelectedRequest(null);
      loadData();
    } catch (error) {
      console.error('Error approving OT:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve OT request');
    }
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    
    try {
      switch (selectedRequest.request_type) {
        case 'leave':
          await leaveAPI.reject(selectedRequest.id, rejectionReason);
          break;
        case 'shift_change':
          await shiftChangeAPI.reject(selectedRequest.id, rejectionReason);
          break;
        case 'schedule_adjustment':
          await scheduleAdjustmentAPI.reject(selectedRequest.id, rejectionReason);
          break;
        case 'time_adjustment':
          await timeAdjustmentAPI.reject(selectedRequest.id, rejectionReason);
          break;
        case 'ot_request':
          await otRequestAPI.reject(selectedRequest.id, rejectionReason);
          break;
        default:
          throw new Error('Unknown request type');
      }
      toast.success('Request rejected');
      setRejectDialogOpen(false);
      setSelectedRequest(null);
      loadData();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'clocked_in': return <Badge className="bg-green-100 text-green-800">Clocked In</Badge>;
      case 'clocked_out': return <Badge variant="secondary">Clocked Out</Badge>;
      case 'on_leave': return <Badge className="bg-blue-100 text-blue-800">On Leave</Badge>;
      case 'late': return <Badge className="bg-orange-100 text-orange-800">Late</Badge>;
      case 'absent': return <Badge variant="destructive">Absent</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRequestTypeBadge = (type) => {
    const badges = {
      leave: { label: 'Leave', color: 'bg-blue-100 text-blue-800' },
      shift_change: { label: 'Shift Change', color: 'bg-purple-100 text-purple-800' },
      schedule_adjustment: { label: 'Schedule', color: 'bg-orange-100 text-orange-800' },
      time_adjustment: { label: 'Time', color: 'bg-green-100 text-green-800' },
      ot_request: { label: 'OT Request', color: 'bg-amber-100 text-amber-800' }
    };
    const badge = badges[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={badge.color}>{badge.label}</Badge>;
  };

  const stats = dashboardData?.stats || {};
  const teamStatus = dashboardData?.team_status || [];

  return (
    <div className="space-y-6" data-testid="my-team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Team</h1>
          <p className="text-muted-foreground">Monitor your team and manage approvals</p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-2">
            <Users className="h-4 w-4" />
            Team Dashboard
          </TabsTrigger>
          <TabsTrigger value="approvals" className="gap-2">
            <ClipboardList className="h-4 w-4" />
            Approval Inbox
            {stats.pending_approvals > 0 && (
              <Badge variant="destructive" className="ml-1">{stats.pending_approvals}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Team Size</p>
                    <p className="text-xl font-bold">{stats.total_team_members || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <UserCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Present</p>
                    <p className="text-xl font-bold">{stats.present_today || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Absent</p>
                    <p className="text-xl font-bold">{stats.absent_today || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Clock className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Late</p>
                    <p className="text-xl font-bold">{stats.late_today || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Coffee className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">On Leave</p>
                    <p className="text-xl font-bold">{stats.on_leave_today || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">OT Hours</p>
                    <p className="text-xl font-bold">{stats.total_ot_hours_today?.toFixed(1) || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Team Status Table */}
          <Card>
            <CardHeader>
              <CardTitle>Team Status - Today</CardTitle>
              <CardDescription>Real-time status of your direct reports</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : teamStatus.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No team members found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Clock In</TableHead>
                      <TableHead>Late (min)</TableHead>
                      <TableHead>Current Task</TableHead>
                      <TableHead>OT (min)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teamStatus.map((member) => (
                      <TableRow key={member.employee_id} data-testid="team-member-row">
                        <TableCell className="font-medium">{member.employee_name}</TableCell>
                        <TableCell>{member.department_name}</TableCell>
                        <TableCell>{getStatusBadge(member.status)}</TableCell>
                        <TableCell>
                          {member.clock_in_time 
                            ? format(new Date(member.clock_in_time), 'h:mm a')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {member.late_minutes > 0 ? (
                            <span className="text-orange-600 font-medium">{member.late_minutes}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-sm max-w-[150px] truncate">
                          {member.current_task || '-'}
                        </TableCell>
                        <TableCell>
                          {member.ot_minutes_today > 0 ? (
                            <span className="text-blue-600 font-medium">{member.ot_minutes_today}</span>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approvals Tab */}
        <TabsContent value="approvals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Pending Approvals ({approvals.length})
              </CardTitle>
              <CardDescription>Review and approve or reject team requests</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : approvals.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50 text-green-500" />
                  <p>No pending approvals</p>
                  <p className="text-sm">All requests have been processed</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-4">
                    {approvals.map((request) => (
                      <Card key={`${request.request_type}-${request.id}`} className="border-l-4 border-l-primary">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                {getRequestTypeBadge(request.request_type)}
                                <span className="font-medium">{request.employee_name}</span>
                                {request.status === 'manager_approved' && (
                                  <Badge variant="outline" className="text-orange-600">Awaiting HR</Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Submitted {request.submitted_at ? format(new Date(request.submitted_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                              </div>
                              
                              {/* Request Details */}
                              <div className="mt-2 p-3 bg-muted/50 rounded-lg text-sm">
                                {request.request_type === 'leave' && (
                                  <>
                                    <p><strong>Type:</strong> {request.details.leave_type}</p>
                                    <p><strong>Dates:</strong> {request.details.start_date} to {request.details.end_date}</p>
                                    <p><strong>Reason:</strong> {request.details.reason || 'N/A'}</p>
                                  </>
                                )}
                                {request.request_type === 'shift_change' && (
                                  <>
                                    <p><strong>Date:</strong> {request.details.date}</p>
                                    <p><strong>Requested Shift:</strong> {request.details.requested_shift}</p>
                                    <p><strong>Reason:</strong> {request.details.reason || 'N/A'}</p>
                                  </>
                                )}
                                {request.request_type === 'schedule_adjustment' && (
                                  <>
                                    <p><strong>Type:</strong> {request.details.type}</p>
                                    <p><strong>Dates:</strong> {request.details.start_date}{request.details.end_date ? ` to ${request.details.end_date}` : ''}</p>
                                    <p><strong>Requested Shift:</strong> {request.details.requested_shift}</p>
                                    {request.details.is_recurring && <p><strong>Recurring:</strong> Yes</p>}
                                    <p><strong>Reason:</strong> {request.details.reason || 'N/A'}</p>
                                  </>
                                )}
                                {request.request_type === 'time_adjustment' && (
                                  <>
                                    <p><strong>Date:</strong> {request.details.date}</p>
                                    <p><strong>Original:</strong> In: {request.details.original_clock_in ? format(new Date(request.details.original_clock_in), 'h:mm a') : 'N/A'} | Out: {request.details.original_clock_out ? format(new Date(request.details.original_clock_out), 'h:mm a') : 'N/A'}</p>
                                    <p className="text-green-600"><strong>Proposed:</strong> In: {request.details.proposed_clock_in ? format(new Date(request.details.proposed_clock_in), 'h:mm a') : '-'} | Out: {request.details.proposed_clock_out ? format(new Date(request.details.proposed_clock_out), 'h:mm a') : '-'}</p>
                                    <p><strong>Reason:</strong> {request.details.reason || 'N/A'}</p>
                                  </>
                                )}
                                {request.request_type === 'ot_request' && (
                                  <>
                                    <p><strong>Date:</strong> {request.details.date} {request.details.is_holiday_ot && <Badge variant="outline" className="ml-2 text-amber-600 border-amber-600">Holiday OT</Badge>}</p>
                                    <p><strong>Requested Hours:</strong> {request.details.requested_hours}h</p>
                                    {request.details.start_time && request.details.end_time && (
                                      <p><strong>Time:</strong> {request.details.start_time} - {request.details.end_time}</p>
                                    )}
                                    {request.details.project_code && (
                                      <p><strong>Project:</strong> {request.details.project_code}</p>
                                    )}
                                    <p><strong>Reason:</strong> {request.details.reason || 'N/A'}</p>
                                    {request.details.holiday_multiplier && (
                                      <p className="text-amber-600"><strong>Holiday Multiplier:</strong> {request.details.holiday_multiplier}x</p>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => openRejectDialog(request)}
                                data-testid="reject-btn"
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                              <Button 
                                size="sm"
                                onClick={() => handleApprove(request)}
                                data-testid="approve-btn"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                {request.status === 'manager_approved' ? 'HR Approve' : 'Approve'}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Request</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this request (optional but recommended)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea 
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject}>Reject Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OT Approval Dialog (for partial approval) */}
      <Dialog open={otApprovalDialogOpen} onOpenChange={setOtApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Overtime Request</DialogTitle>
            <DialogDescription>
              You can approve the full requested hours or adjust them. Leave blank to approve the full {selectedRequest?.details?.requested_hours}h requested.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                <strong>Employee:</strong> {selectedRequest?.employee_name}<br />
                <strong>Date:</strong> {selectedRequest?.details?.date}<br />
                <strong>Requested:</strong> {selectedRequest?.details?.requested_hours}h<br />
                <strong>Reason:</strong> {selectedRequest?.details?.reason}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Approved Hours</Label>
              <Input 
                type="number"
                step="0.5"
                min="0.5"
                max={selectedRequest?.details?.requested_hours}
                value={otApprovalHours}
                onChange={(e) => setOtApprovalHours(e.target.value)}
                placeholder={`Full ${selectedRequest?.details?.requested_hours}h`}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to approve the full requested hours
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOtApprovalDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleOtApprove} data-testid="confirm-ot-approve-btn">
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve OT
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTeamPage;
